export const DEFAULT_VAT_RATE = 0.2;

export type PromoDiscountType = "percentage" | "fixed";

interface PromoDiscountInput {
  cartTotalHt: number;
  eligibleTotalHt: number;
  discountType: PromoDiscountType;
  discountValue: number;
  minOrderHt?: number | null;
}

interface OrderTotalsInput {
  articlesHt: number;
  promoDiscountHt?: number;
  shippingTtc?: number;
  insuranceWithoutTax?: number;
  vatRate?: number;
}

/**
 * Calcule une remise exprimée en HT et la borne au montant des articles
 * éligibles. Les promotions fixes sont elles aussi enregistrées en HT.
 */
export function calculatePromoDiscountHt({
  cartTotalHt,
  eligibleTotalHt,
  discountType,
  discountValue,
  minOrderHt,
}: PromoDiscountInput): number {
  const safeCartTotal = Math.max(0, cartTotalHt);
  const safeEligibleTotal = Math.max(0, eligibleTotalHt);
  const safeDiscountValue = Math.max(0, discountValue);

  if (minOrderHt && safeCartTotal < minOrderHt) return 0;

  const discount = discountType === "percentage"
    ? safeEligibleTotal * (safeDiscountValue / 100)
    : safeDiscountValue;

  return Math.min(safeEligibleTotal, Math.max(0, discount));
}

/**
 * Source de vérité pour les récapitulatifs panier/checkout.
 *
 * - articles et promotions : HT, soumis à TVA ;
 * - tarifs logistiques : stockés TTC, puis ventilés en HT/TVA ;
 * - assurance location : non soumise à TVA.
 */
export function calculateOrderTotals({
  articlesHt,
  promoDiscountHt = 0,
  shippingTtc = 0,
  insuranceWithoutTax = 0,
  vatRate = DEFAULT_VAT_RATE,
}: OrderTotalsInput) {
  const safeArticlesHt = Math.max(0, articlesHt);
  const safeDiscountHt = Math.min(safeArticlesHt, Math.max(0, promoDiscountHt));
  const safeShippingTtc = Math.max(0, shippingTtc);
  const safeInsurance = Math.max(0, insuranceWithoutTax);
  const discountedArticlesHt = safeArticlesHt - safeDiscountHt;
  const shippingHt = safeShippingTtc / (1 + vatRate);
  const shippingVat = safeShippingTtc - shippingHt;
  const vat = discountedArticlesHt * vatRate + shippingVat;
  const totalTtc = discountedArticlesHt + shippingHt + vat + safeInsurance;

  return {
    articlesHt: safeArticlesHt,
    promoDiscountHt: safeDiscountHt,
    discountedArticlesHt,
    shippingHt,
    shippingVat,
    taxableTotalHt: discountedArticlesHt + shippingHt,
    insuranceWithoutTax: safeInsurance,
    vat,
    totalTtc,
  };
}
